import React from 'react';
import { Link } from 'react-router-dom';
import { Icon, IconButton, Badge } from '@openedx/paragon';
import {
  RiBookOpenLine,
  RiGraduationCapLine,
  RiRouteLine,
  RiTimerLine,
  RiStarFill,
  RiHeart3Line,
  RiHeart3Fill,
} from '@remixicon/react';
import { getFeatureToggle } from '@blend-ed/blendxplatform/utils';
import { FormattedMessage, useIntl } from '@edx/frontend-platform/i18n';

import { useCart } from '../../../gql/hooks/useCart';
import RegionalPriceLabel from './RegionalPriceLabel';
import messages from './messages';

const TYPE_ICONS = {
  Course: RiBookOpenLine,
  Program: RiGraduationCapLine,
  Pathway: RiRouteLine,
};

const DiscoverCard = ({
  id,
  contentId,
  title,
  imageUrl,
  type,
  duration,
  rating,
  reviewCount,
  skills,
  price,
  isNew,
  subscriptionOnly = false,
  inviteOnly = false,
}) => {
  const { formatMessage } = useIntl();
  const featuresToggled = getFeatureToggle();
  const paymentsEnabled = !featuresToggled.loading && featuresToggled.features_payments === true;
  const {
    coursesInWishlist,
    programsInWishlist,
    setCourseCartState,
    setProgramCartState,
    pendingTarget,
    canMutate,
  } = useCart();

  const TypeIcon = TYPE_ICONS[type] || RiBookOpenLine;
  const isPathway = type === 'Pathway';
  let path = `/discover/${encodeURIComponent(id)}`;
  if (type === 'Program') {
    path = `/discover/program/${encodeURIComponent(id)}`;
  } else if (isPathway) {
    path = `/discover/pathway/${encodeURIComponent(id)}`;
  }
  const wrapperProps = {
    to: path,
    className: 'discover-card',
  };

  const wishlistKey = contentId || id;
  let isWishlisted = false;
  if (type === 'Program') {
    isWishlisted = programsInWishlist.has(wishlistKey);
  } else if (!isPathway) {
    isWishlisted = coursesInWishlist.has(wishlistKey);
  }

  const handleWishlistClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!wishlistKey) return;
    if (type === 'Program') {
      setProgramCartState(wishlistKey, 'wishlist');
    } else {
      setCourseCartState(wishlistKey, 'wishlist');
    }
  };

  return (
    <Link {...wrapperProps}>
      <div
        className="discover-card__image"
        style={{ backgroundImage: `url(${imageUrl})` }}
        role="img"
        aria-label={title}
      >
        <div className="discover-card__type-badge">
          <Icon
            className="discover-card__type-badge-icon"
            src={TypeIcon}
            size="xs"
          />
          <span className="discover-card__type-badge-text">{type}</span>
        </div>
        
        {isNew && (
          <div className="discover-card__new-badge">
            <Badge variant="soft" color="success" size="sm">
              {formatMessage(messages.newBadge)}
            </Badge>
          </div>
        )}

        {!isPathway && (
          <IconButton
            src={isWishlisted ? RiHeart3Fill : RiHeart3Line}
            iconAs={Icon}
            alt={
              isWishlisted
                ? formatMessage(messages.removeFromWishlist)
                : formatMessage(messages.addToWishlist)
            }
            size="sm"
            variant="soft"
            color="neutral"
            className={`discover-card__wishlist-btn${
              isWishlisted ? ' discover-card__wishlist-btn--active' : ''
            }`}
            onClick={handleWishlistClick}
            disabled={!canMutate || pendingTarget === 'wishlist'}
          />
        )}
      </div>

      <div className="discover-card__body">
        <h3 className="discover-card__title">{title}</h3>

        <div className="discover-card__meta">
          {duration && (
            <>
              <span className="discover-card__duration">
                <Icon src={RiTimerLine} size="xs" />
                {duration}
              </span>
              <span className="discover-card__dot" />
            </>
          )}
          {rating != null ? (
            <span className="discover-card__rating">
              <Icon src={RiStarFill} size="xs" />
              {formatMessage(messages.ratingReviews, { rating, count: reviewCount })}
            </span>
          ) : (
            <span className="discover-card__rating discover-card__rating--empty">
              <FormattedMessage {...messages.noReviews} />
            </span>
          )}
        </div>

        <div className="discover-card__skills">
          {skills.slice(0, 2).map((skill) => (
            <Badge key={skill} size="xs" variant="soft" color="gray" className="discover-card__skill-badge">
              {skill}
            </Badge>
          ))}
          {skills.length > 2 && (
            <Badge size="xs" variant="soft" color="gray" className="discover-card__skill-badge">
              <FormattedMessage
                {...messages.extraSkillsCount}
                values={{ count: skills.length - 2 }}
              />
            </Badge>
          )}
        </div>

        {paymentsEnabled && (
          <div className="discover-card__price">
            {/* A one-time price is meaningless for these: invite-only can't be
                bought at all, and subscription-only carries minPrice 0, which
                would otherwise render as "Free". */}
            {inviteOnly && formatMessage(messages.inviteOnlyLabel)}
            {!inviteOnly && subscriptionOnly && formatMessage(messages.subscriptionLabel)}
            {/* Regional (PPP) pricing: this dashboard reads prices from DDN,
                which bypasses Django, so the discount is applied client-side
                here rather than arriving pre-discounted like the anonymous
                storefront's. Falls back to the list price on any failure. */}
            {!inviteOnly && !subscriptionOnly && <RegionalPriceLabel price={price} />}
          </div>
        )}
      </div>
    </Link>
  );
};

export default DiscoverCard;
